import type {
  UserProfile,
  Branding,
  ScanResult,
  ScansResponse,
  Report,
  ReportsResponse,
  ReportGenerateResponse,
  SubscriptionInfo,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // For local development, add dev email header
  if (import.meta.env.DEV) {
    headers['X-Dev-Email'] = 'dev@velosent.com';
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      errorData.message || errorData.error || `HTTP ${response.status}`
    );
  }

  return response.json();
}

// User API
export const userApi = {
  getProfile: () => fetchApi<UserProfile>('/user/me'),

  updateBranding: (branding: Partial<Branding>) =>
    fetchApi<{ success: boolean; branding: Branding }>('/user/branding', {
      method: 'PUT',
      body: JSON.stringify(branding),
    }),

  getSubscription: () => fetchApi<SubscriptionInfo>('/user/subscription'),
};

// Scan API
export const scanApi = {
  scanDomain: (domain: string) =>
    fetchApi<ScanResult>('/scan', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    }),

  getScan: (scanId: string) => fetchApi<ScanResult>(`/scan/${scanId}`),

  listScans: (limit = 20, offset = 0) =>
    fetchApi<ScansResponse>(`/scans?limit=${limit}&offset=${offset}`),
};

// Report API
export const reportApi = {
  generate: (scanId: string) =>
    fetchApi<ReportGenerateResponse>('/report/generate', {
      method: 'POST',
      body: JSON.stringify({ scanId }),
    }),

  getReport: (reportId: string) => fetchApi<Report>(`/report/${reportId}`),

  listReports: (limit = 20, offset = 0, status?: string) => {
    let url = `/reports?limit=${limit}&offset=${offset}`;
    if (status) url += `&status=${status}`;
    return fetchApi<ReportsResponse>(url);
  },

  getDownloadUrl: (reportId: string) => `${API_BASE}/report/${reportId}/download`,
};

export { ApiError };
